import React, { useCallback, useEffect, useState } from 'react';
import { UseFormRegister, Control, FieldErrors } from 'react-hook-form';

/**
 * 🏗️ FSD Features Layer - Pricing Tab Content
 *
 * 가격 설정 탭 컨텐츠
 * 강의 가격, 할인, 프로모션 설정
 */

export interface PricingFormData {
  priceAmount: number;
  priceCurrency: string;
  discountPercentage?: number;
  promotionStartDate?: string;
  promotionEndDate?: string;
  isFree: boolean;
}

export interface PricingTabContentProps {
  register?: UseFormRegister<PricingFormData>;
  control?: Control<PricingFormData>;
  errors?: FieldErrors<PricingFormData>;
  disabled?: boolean;
  courseId: string;
  onPriceChange?: (price: PricingFormData) => void;
  value?: Partial<PricingFormData>;
}

export const PricingTabContent: React.FC<PricingTabContentProps> = ({
  register,
  control,
  errors,
  disabled = false,
  courseId,
  onPriceChange,
  value,
}) => {
  const [isFree, setIsFree] = useState(false);
  const [currency, setCurrency] = useState('KRW');
  const [price, setPrice] = useState(0);
  const [hasDiscount, setHasDiscount] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);

  const emitPriceChange = useCallback((next: {
    amount: number;
    currency: string;
    isFree: boolean;
    hasDiscount: boolean;
    discountPercentage: number;
  }) => {
    if (!onPriceChange) {
      return;
    }
    onPriceChange({
      priceAmount: next.isFree ? 0 : next.amount,
      priceCurrency: next.currency,
      discountPercentage: next.hasDiscount ? next.discountPercentage : undefined,
      promotionStartDate: undefined,
      promotionEndDate: undefined,
      isFree: next.isFree,
    });
  }, [onPriceChange]);

  const handleFreeToggle = () => {
    const nextIsFree = !isFree;
    const nextPrice = nextIsFree ? 0 : price;
    const nextHasDiscount = nextIsFree ? false : hasDiscount;
    const nextDiscount = nextHasDiscount ? discountPercentage : 0;

    setIsFree(nextIsFree);
    if (nextIsFree) {
      setPrice(0);
      setHasDiscount(false);
      setDiscountPercentage(0);
    }

    emitPriceChange({
      amount: nextPrice,
      currency,
      isFree: nextIsFree,
      hasDiscount: nextHasDiscount,
      discountPercentage: nextDiscount,
    });
  };

  const calculateDiscountedPrice = () => {
    if (!hasDiscount || discountPercentage === 0) return price;
    return price * (1 - discountPercentage / 100);
  };

  const formatPrice = (amount: number) => {
    if (currency === 'KRW') {
      return `₩${amount.toLocaleString('ko-KR')}`;
    } else if (currency === 'USD') {
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    }
    return `${amount} ${currency}`;
  };

  useEffect(() => {
    if (!value) {
      return;
    }

    const incomingCurrency = value.priceCurrency?.toString().trim().toUpperCase() || 'KRW';
    const rawAmount = typeof value.priceAmount === 'number'
      ? value.priceAmount
      : Number(value.priceAmount ?? 0);
    const incomingAmount = Number.isFinite(rawAmount) ? Math.max(0, rawAmount) : 0;
    const incomingIsFree = value.isFree ?? incomingAmount <= 0;
    const incomingHasDiscount = Boolean(value.discountPercentage && value.discountPercentage > 0);
    const incomingDiscount = incomingHasDiscount ? (value.discountPercentage as number) : 0;

    setIsFree(prev => prev !== incomingIsFree ? incomingIsFree : prev);
    setCurrency(prev => prev !== incomingCurrency ? incomingCurrency : prev);
    setPrice(prev => prev !== incomingAmount ? incomingAmount : prev);
    setHasDiscount(prev => prev !== incomingHasDiscount ? incomingHasDiscount : prev);
    setDiscountPercentage(prev => prev !== incomingDiscount ? incomingDiscount : prev);
  }, [
    value?.priceAmount,
    value?.priceCurrency,
    value?.isFree,
    value?.discountPercentage,
  ]);

  return (
    <div className="pricing-tab-content">
      {/* 무료/유료 선택 */}
      <div className="border-b border-gray-200 pb-6">
        <h3 className="text-lg font-semibold mb-4">가격 유형</h3>

        <div className="flex gap-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="priceType"
              checked={isFree}
              onChange={handleFreeToggle}
              disabled={disabled}
              className="mr-2"
            />
            <span className="text-gray-700">무료 강의</span>
          </label>

          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="priceType"
              checked={!isFree}
              onChange={handleFreeToggle}
              disabled={disabled}
              className="mr-2"
            />
            <span className="text-gray-700">유료 강의</span>
          </label>
        </div>
      </div>

      {/* 가격 설정 */}
      {!isFree && (
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-semibold mb-4">가격 설정</h3>

          <div className="grid grid-cols-2 gap-4">
            {/* 통화 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                통화
              </label>
              <select
                value={currency}
                onChange={(e) => {
                  const nextCurrency = e.target.value;
                  setCurrency(nextCurrency);
                  emitPriceChange({
                    amount: price,
                    currency: nextCurrency,
                    isFree,
                    hasDiscount,
                    discountPercentage,
                  });
                }}
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="KRW">한국 원화 (₩)</option>
                <option value="USD">미국 달러 ($)</option>
                <option value="EUR">유로 (€)</option>
                <option value="JPY">일본 엔 (¥)</option>
              </select>
            </div>

            {/* 가격 입력 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                판매 가격
              </label>
              <input
                type="number"
                value={price}
                onChange={(e) => {
                  const nextValue = Number(e.target.value);
                  const nextAmount = Number.isFinite(nextValue) ? Math.max(0, nextValue) : 0;
                  setPrice(nextAmount);
                  emitPriceChange({
                    amount: nextAmount,
                    currency,
                    isFree,
                    hasDiscount,
                    discountPercentage,
                  });
                }}
                disabled={disabled}
                min="0"
                step={currency === 'KRW' ? '1000' : '1'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={currency === 'KRW' ? '50000' : '49.99'}
              />
            </div>
          </div>

          {/* 현재 가격 표시 */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">설정된 가격:</span>
              <span className="text-2xl font-bold text-gray-900">
                {formatPrice(price)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 할인 설정 */}
      {!isFree && price > 0 && (
        <div className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">할인 프로모션</h3>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={hasDiscount}
                onChange={(e) => {
                  const nextHasDiscount = e.target.checked;
                  setHasDiscount(nextHasDiscount);
                  const nextDiscount = nextHasDiscount ? discountPercentage : 0;
                  if (!nextHasDiscount) {
                    setDiscountPercentage(0);
                  }
                  emitPriceChange({
                    amount: price,
                    currency,
                    isFree,
                    hasDiscount: nextHasDiscount,
                    discountPercentage: nextDiscount,
                  });
                }}
                disabled={disabled}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">할인 적용</span>
            </label>
          </div>

          {hasDiscount && (
            <>
              {/* 할인율 설정 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  할인율 (%)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="5"
                    max="90"
                    step="5"
                    value={discountPercentage}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setDiscountPercentage(next);
                      emitPriceChange({
                        amount: price,
                        currency,
                        isFree,
                        hasDiscount,
                        discountPercentage: next,
                      });
                    }}
                    disabled={disabled}
                    className="flex-1"
                  />
                  <span className="w-16 text-center font-semibold">
                    {discountPercentage}%
                  </span>
                </div>
              </div>

              {/* 할인 기간 설정 */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    프로모션 시작일
                  </label>
                  <input
                    type="date"
                    disabled={disabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    프로모션 종료일
                  </label>
                  <input
                    type="date"
                    disabled={disabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* 할인 가격 표시 */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">원가:</span>
                  <span className="text-lg line-through text-gray-500">
                    {formatPrice(price)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-green-700 font-medium">할인가:</span>
                  <span className="text-2xl font-bold text-green-700">
                    {formatPrice(calculateDiscountedPrice())}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* 수익 예측 */}
      {!isFree && price > 0 && (
        <div className="pb-6">
          <h3 className="text-lg font-semibold mb-4">수익 예측</h3>

          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">10명 수강 시:</span>
              <span className="font-medium">
                {formatPrice(calculateDiscountedPrice() * 10)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">50명 수강 시:</span>
              <span className="font-medium">
                {formatPrice(calculateDiscountedPrice() * 50)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">100명 수강 시:</span>
              <span className="font-medium">
                {formatPrice(calculateDiscountedPrice() * 100)}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">500명 수강 시:</span>
              <span className="font-bold text-lg">
                {formatPrice(calculateDiscountedPrice() * 500)}
              </span>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              💡 플랫폼 수수료 20% 제외 전 금액입니다.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingTabContent;
